import { clamp } from '../sim/math.js';
export function defaultModifiers() {
    return {
        speedScale: 1,
        turnScale: 1,
        damageScale: 1,
        damageTakenScale: 1,
        fireRateScale: 1,
        heatScale: 1,
        dissipationScale: 1,
        spreadScale: 1,
        armorBonus: 0,
        cooldownScale: 1,
        jetThrustScale: 1,
        jetCostScale: 1,
        noBoost: false,
        noJump: false,
        noAbilities: false,
        noShieldRegen: false,
        noWeapons: false,
        immobile: false,
        noTurn: false,
        ccImmune: false,
        cloaked: false,
        revealed: false,
        invulnerable: false,
        heatLock: false,
        noUltCharge: false,
        stunned: false,
        dash: null,
        parry: null,
        impulse: null,
    };
}
function setFlag(m, flag) {
    switch (flag) {
        case 'noBoost':
            m.noBoost = true;
            break;
        case 'noJump':
            m.noJump = true;
            break;
        case 'noAbilities':
            m.noAbilities = true;
            break;
        case 'noShieldRegen':
            m.noShieldRegen = true;
            break;
        case 'noWeapons':
            m.noWeapons = true;
            break;
        case 'immobile':
            m.immobile = true;
            break;
        case 'noTurn':
            m.noTurn = true;
            break;
        case 'ccImmune':
            m.ccImmune = true;
            break;
        case 'cloaked':
            m.cloaked = true;
            break;
        case 'revealed':
            m.revealed = true;
            break;
        case 'invulnerable':
            m.invulnerable = true;
            break;
        case 'heatLock':
            m.heatLock = true;
            break;
        case 'noUltCharge':
            m.noUltCharge = true;
            break;
        case 'stunned':
            m.stunned = true;
            break;
    }
}
/** Leg damage slows up to `maxSlow` once it reaches `atHpFraction` of max HP (linear). */
export function legSlowFactor(legDamage, maxHp, balance) {
    const full = maxHp * balance.legDamage.atHpFraction;
    const t = full > 0 ? clamp(legDamage / full, 0, 1) : 0;
    return 1 - balance.legDamage.maxSlow * t;
}
/** Arm damage widens spread up to `maxSpread` (0.6 = +60 %) at `atHpFraction` of max HP. */
export function armSpreadFactor(armDamage, maxHp, balance) {
    const full = maxHp * balance.armDamage.atHpFraction;
    const t = full > 0 ? clamp(armDamage / full, 0, 1) : 0;
    return 1 + balance.armDamage.maxSpread * t;
}
/** Folds a bounded stat-mod record (a module set) into `m` before any status effect. */
export function applyBaseMods(m, base) {
    if (base.speedScale !== undefined)
        m.speedScale *= base.speedScale;
    if (base.turnScale !== undefined)
        m.turnScale *= base.turnScale;
    if (base.damageScale !== undefined)
        m.damageScale *= base.damageScale;
    if (base.damageTakenScale !== undefined)
        m.damageTakenScale *= base.damageTakenScale;
    if (base.fireRateScale !== undefined)
        m.fireRateScale *= base.fireRateScale;
    if (base.heatScale !== undefined)
        m.heatScale *= base.heatScale;
    if (base.dissipationScale !== undefined)
        m.dissipationScale *= base.dissipationScale;
    if (base.spreadScale !== undefined)
        m.spreadScale *= base.spreadScale;
    if (base.armorBonus !== undefined)
        m.armorBonus += base.armorBonus;
    if (base.cooldownScale !== undefined)
        m.cooldownScale *= base.cooldownScale;
    if (base.jetThrustScale !== undefined)
        m.jetThrustScale *= base.jetThrustScale;
    if (base.jetCostScale !== undefined)
        m.jetCostScale *= base.jetCostScale;
    if (base.flags)
        for (const f of base.flags)
            setFlag(m, f);
}
/**
 * The per-tick modifiers: the combatant's module set (`base`, Phase 6) first, then its status
 * effects, then the location damage penalties.
 */
export function computeModifiers(c, def, balance, base) {
    const m = defaultModifiers();
    if (base)
        applyBaseMods(m, base);
    for (const e of c.effects) {
        const p = e.params;
        if (p.speedScale !== undefined)
            m.speedScale *= p.speedScale;
        if (p.turnScale !== undefined)
            m.turnScale *= p.turnScale;
        if (p.damageScale !== undefined)
            m.damageScale *= p.damageScale;
        if (p.damageTakenScale !== undefined)
            m.damageTakenScale *= p.damageTakenScale;
        if (p.fireRateScale !== undefined)
            m.fireRateScale *= p.fireRateScale;
        if (p.heatScale !== undefined)
            m.heatScale *= p.heatScale;
        if (p.dissipationScale !== undefined)
            m.dissipationScale *= p.dissipationScale;
        if (p.spreadScale !== undefined)
            m.spreadScale *= p.spreadScale;
        if (p.armorBonus !== undefined)
            m.armorBonus += p.armorBonus;
        if (p.cooldownScale !== undefined)
            m.cooldownScale *= p.cooldownScale;
        if (p.jetThrustScale !== undefined)
            m.jetThrustScale *= p.jetThrustScale;
        if (p.jetCostScale !== undefined)
            m.jetCostScale *= p.jetCostScale;
        if (p.flags)
            for (const f of p.flags)
                setFlag(m, f);
        if (p.dash)
            m.dash = p.dash;
        if (p.parry)
            m.parry = p.parry;
        if (p.impulse) {
            m.impulse = m.impulse
                ? {
                    x: m.impulse.x + p.impulse.x,
                    y: m.impulse.y + p.impulse.y,
                    z: m.impulse.z + p.impulse.z,
                }
                : { ...p.impulse };
        }
    }
    if (m.stunned) {
        m.immobile = true;
        m.noTurn = true;
        m.noWeapons = true;
        m.noAbilities = true;
    }
    m.speedScale *= legSlowFactor(c.legDamage, def.hp, balance);
    m.spreadScale *= armSpreadFactor(c.armDamage, def.hp, balance);
    return m;
}
export function moveModifiersOf(m) {
    return {
        speedScale: m.speedScale,
        turnScale: m.turnScale,
        jetThrustScale: m.jetThrustScale,
        jetCostScale: m.jetCostScale,
        noBoost: m.noBoost,
        noJump: m.noJump,
        immobile: m.immobile,
        noTurn: m.noTurn,
        dash: m.dash ? { vx: m.dash.vx, vz: m.dash.vz } : null,
        impulse: m.impulse,
    };
}
//# sourceMappingURL=modifiers.js.map